import React, { useEffect, useState, useCallback } from 'react';
import { AnimatePresence } from 'framer-motion';
import InstallPrompt from './components/InstallPrompt';
import { useInstallPrompt } from './hooks/useInstallPrompt';
import { useChat } from './hooks/useChat';
import { useVoice } from './hooks/useVoice';
import { useAmbientAudio } from './hooks/useAmbientAudio';
import { usePushNotifications } from './hooks/usePushNotifications';
import AuthPage from './components/AuthPage';
import ProfileCompletePage from './components/ProfileCompletePage';
import SplashScreen from './components/SplashScreen';
import TopBar from './components/TopBar';
import CenterOrb from './components/CenterOrb';
import BottomInput from './components/BottomInput';
import MessageList from './components/MessageList';
import VoiceMode from './components/VoiceMode';
import SettingsPanel from './components/SettingsPanel';
import GlowEffect from './components/GlowEffect';
import SparkleEffect from './components/SparkleEffect';
import Background from './components/Background';
import AgentPicker from './components/AgentPicker';
import { useUser, AuthenticateWithRedirectCallback } from '@clerk/clerk-react';
import { API_BASE, getMe, logout, getAgentConfig, clerkSync, hasSessionToken, updateUserSettings } from './services/api';
import './App.css';

export default function App() {
  // Handle SSO callback route - must be checked before any other rendering
  if (window.location.pathname === '/sso-callback') {
    return <AuthenticateWithRedirectCallback />;
  }

  return <AppContent />;
}

function AppContent() {
  const [splashDone, setSplashDone] = useState(false);
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [needsProfile, setNeedsProfile] = useState(false);
  const [needsAgentPicker, setNeedsAgentPicker] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [instanceStatus, setInstanceStatus] = useState(null);
  const [needsPushPermission, setNeedsPushPermission] = useState(false);
  const { requestPermission: requestPushPermission } = usePushNotifications();
  const { showPrompt: showInstallPrompt, dismiss: dismissInstallPrompt } = useInstallPrompt(!!user && !!user.profile_complete);
  const [clerkSyncError, setClerkSyncError] = useState(null);
  const [clerkSyncing, setClerkSyncing] = useState(false);

  const { user: clerkUser, isSignedIn: clerkSignedIn, isLoaded: clerkLoaded } = useUser();

  const {
    messages, isLoading, mode, setMode,
    sendMessage, animationPhase, loadHistory,
    isAudioPlaying
  } = useChat();

  const {
    isListening, isMuted, transcript, committedTranscript,
    partialTranscript, isSpeaking, volume, vadScore,
    startListening, stopListening, toggleMute, clearTranscript,
  } = useVoice();

  const { playing: ambientPlaying, toggleAmbient, duckVolume, restoreVolume } = useAmbientAudio();

  // Volume Ducking Logic
  useEffect(() => {
    if (isAudioPlaying || animationPhase === 'responding') {
      duckVolume();
    } else {
      restoreVolume();
    }
  }, [isAudioPlaying, animationPhase, duckVolume, restoreVolume]);

  // Initial auth check — try to restore session from localStorage token or cookie
  useEffect(() => {
    // Handle auto_token from push notification tap
    const params = new URLSearchParams(window.location.search);
    const autoToken = params.get('auto_token');
    if (autoToken) {
      localStorage.setItem('oimy_session_token', autoToken);
      params.delete('auto_token');
      const newUrl = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }

    if (!hasSessionToken()) {
      // No stored token — skip the getMe() call, wait for Clerk or manual login
      setAuthChecked(true);
      return;
    }
    getMe().then(u => {
      setUser(u);
      if (!u.profile_complete) {
        setNeedsProfile(true);
      } else if (!localStorage.getItem('oimy_selected_agent')) {
        setNeedsAgentPicker(true);
      } else if (
        typeof Notification !== 'undefined' &&
        Notification.permission === 'default' &&
        !localStorage.getItem('oimy_push_dismissed')
      ) {
        setNeedsPushPermission(true);
      }
    }).catch((err) => {
      console.warn('getMe() failed (no valid session):', err.message);
    }).finally(() => setAuthChecked(true));
  }, []);

  // Clerk OAuth sync: when Clerk user exists but no local session, sync to backend
  useEffect(() => {
    if (!clerkLoaded) return; // Wait for Clerk to load
    if (clerkSignedIn && clerkUser && !user && authChecked) {
      console.log('[Auth] Clerk signed in, syncing to backend...');

      // Try all available email sources — primaryEmailAddress can be null for some Google accounts
      const email =
        clerkUser.primaryEmailAddress?.emailAddress ||
        clerkUser.emailAddresses?.[0]?.emailAddress ||
        clerkUser.externalAccounts?.[0]?.emailAddress;

      if (!email) {
        console.error('[Auth] No email found on Clerk user:', clerkUser);
        setClerkSyncError('Could not retrieve your email from Google. Please try signing in with email instead.');
        return;
      }

      const name = clerkUser.fullName || clerkUser.firstName || clerkUser.lastName || 'User';

      setClerkSyncError(null);
      setClerkSyncing(true);

      clerkSync(clerkUser.id, email, name, clerkUser.imageUrl)
        .then(u => {
          console.log('[Auth] Clerk sync succeeded:', u);
          setUser(u);
          if (!u.profile_complete) {
            setNeedsProfile(true);
          } else if (!localStorage.getItem('oimy_selected_agent')) {
            setNeedsAgentPicker(true);
          } else if (
            typeof Notification !== 'undefined' &&
            Notification.permission === 'default' &&
            !localStorage.getItem('oimy_push_dismissed')
          ) {
            setNeedsPushPermission(true);
          }
        })
        .catch(err => {
          console.error('[Auth] Clerk sync failed:', err);
          setClerkSyncError(err.message || 'Google sign-in failed. Please try again or use email login.');
        })
        .finally(() => setClerkSyncing(false));
    }
  }, [clerkSignedIn, clerkUser, clerkLoaded, user, authChecked]);

  useEffect(() => { if (user && user.profile_complete) loadHistory(); }, [user, mode, loadHistory]);

  // Poll instance health every 30 seconds
  useEffect(() => {
    if (!user || !user.profile_complete) return;
    let cancelled = false;
    const fetchHealth = () => {
      getAgentConfig()
        .then(config => { if (!cancelled) setInstanceStatus(config); })
        .catch(() => {});
    };
    fetchHealth();
    const interval = setInterval(fetchHealth, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [user]);

  const handleAuthSuccess = useCallback((u) => {
    setUser(u);
    if (!u.profile_complete) setNeedsProfile(true);
  }, []);
  const handlePreviewEnter = useCallback(() => {
    const previewUser = {
      id: 'preview-user',
      email: 'preview@local',
      name: 'Preview User',
      profile_complete: true,
    };
    setUser(previewUser);
    setNeedsProfile(false);
  }, []);
  const handleProfileComplete = useCallback((u) => { 
    setUser(u); 
    setNeedsProfile(false);
    // After profile complete, show agent picker (if not already selected)
    const savedAgent = localStorage.getItem('oimy_selected_agent');
    if (!savedAgent) {
      setNeedsAgentPicker(true);
    } else {
      setSelectedAgent(JSON.parse(savedAgent));
    }
  }, []);
  
  const handleAgentSelect = useCallback((agent) => {
    setSelectedAgent(agent);
    localStorage.setItem('oimy_selected_agent', JSON.stringify(agent));
    setNeedsAgentPicker(false);
    // Prompt for push permission after agent selection
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      setNeedsPushPermission(true);
    }
  }, []);
  const handleLogout = useCallback(() => {
    logout()
      .catch(() => {})
      .finally(() => { setUser(null); setShowSettings(false); });
  }, []);
  const handleSend = useCallback((text, inputType = 'text') => sendMessage(text, inputType), [sendMessage]);
  const handleVoiceSend = useCallback((text) => {
    if (text.trim()) sendMessage(text, 'voice');
  }, [sendMessage]);

  const isGlowing = ['glowing', 'sparkling', 'responding'].includes(animationPhase);
  const sparkleActive = ['sparkling', 'responding'].includes(animationPhase);
  
  const showOrb = messages.length === 0 && !isListening;

  // Splash screen — shows on every app load, before anything else
  // (SplashScreen auto-skips on SSO callback URLs)
  if (!splashDone) {
    return <SplashScreen onComplete={() => setSplashDone(true)} />;
  }

  if (!authChecked) return <div className="mobile-shell"><div className="app-container"><Background /></div></div>;

  if (!user) {
    return (
      <div className="mobile-shell">
        <div className="app-container">
          <Background />
          <AuthPage
            onAuth={handleAuthSuccess}
            onPreview={handlePreviewEnter}
            googleError={clerkSyncError}
            googleLoading={clerkSyncing}
          />
        </div>
      </div>
    );
  }

  if (needsProfile) {
    return (
      <div className="mobile-shell">
        <div className="app-container chat-background">
          <Background />
          <ProfileCompletePage
            user={user}
            onComplete={handleProfileComplete}
            onSwitchAccount={handleLogout}
            onSkip={handlePreviewEnter}
          />
        </div>
      </div>
    );
  }

  // Agent picker screen - shown after login/profile, before chat
  if (needsAgentPicker) {
    return (
      <div className="mobile-shell">
        <div className="app-container chat-background">
          <Background />
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
            zIndex: 10,
          }}>
            <AgentPicker 
              onSelect={handleAgentSelect}
              currentAgentId={selectedAgent?.agentId}
            />
          </div>
        </div>
      </div>
    );
  }

  if (needsPushPermission) {
    return (
      <div className="mobile-shell">
        <div className="app-container chat-background">
          <Background />
          <div style={{
            position: 'absolute', inset: 0, display: 'flex',
            flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: 24, zIndex: 10
          }}>
            <div style={{
              background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(20px)',
              borderRadius: 28, padding: '32px 24px', maxWidth: 340, textAlign: 'center',
              border: '1px solid rgba(255,255,255,0.15)'
            }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🔔</div>
              <h2 style={{ color: 'white', fontSize: 22, marginBottom: 8 }}>Stay on track</h2>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14, marginBottom: 24 }}>
                Allow notifications so your Oi agent can send you reminders, check-ins, and updates even when the app is closed.
              </p>
              <button onClick={async () => {
                await requestPushPermission();
                setNeedsPushPermission(false);
              }} style={{
                width: '100%', padding: '14px', borderRadius: 24, border: 'none',
                background: 'rgba(255,255,255,0.9)', color: '#000', fontSize: 16,
                fontWeight: 600, cursor: 'pointer', marginBottom: 12
              }}>Allow Notifications</button>
              <button onClick={() => { localStorage.setItem('oimy_push_dismissed', '1'); setNeedsPushPermission(false); }} style={{
                width: '100%', padding: '12px', borderRadius: 24,
                border: '1px solid rgba(255,255,255,0.3)', background: 'transparent',
                color: 'rgba(255,255,255,0.6)', fontSize: 14, cursor: 'pointer'
              }}>Not now</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-shell">
      <div className="app-container chat-background">
        {isGlowing && <GlowEffect />}

        <TopBar
          mode={mode} onModeChange={setMode}
          audioPlaying={ambientPlaying} onAudioToggle={toggleAmbient}
          onSettingsOpen={() => setShowSettings(true)}
          instanceStatus={instanceStatus}
        />

        {showOrb && <CenterOrb />}

        <MessageList
          messages={messages}
          isLoading={isLoading}
          voiceActive={isListening}
          animationPhase={animationPhase}
        />

        {sparkleActive && <SparkleEffect phase={animationPhase} />}

        {/* Toggle between BottomInput and VoiceMode */}
        {!isListening ? (
          <BottomInput
            onSend={handleSend} isLoading={isLoading}
            onVoiceStart={startListening} mode={mode}
            userName={user?.name?.split(' ')[0] || ''}
          />
        ) : (
          <VoiceMode
            onEnd={stopListening} onSendVoice={handleVoiceSend}
            transcript={committedTranscript} partialTranscript={partialTranscript}
            isSpeaking={isSpeaking} isMuted={isMuted} volume={volume}
            vadScore={vadScore} onToggleMute={toggleMute}
            onClearTranscript={clearTranscript}
          />
        )}

        <SettingsPanel
          isOpen={showSettings} onClose={() => setShowSettings(false)}
          user={user} onLogout={handleLogout}
          instanceStatus={instanceStatus}
          onInstanceStatusChange={setInstanceStatus}
        />

        <AnimatePresence>
          {showInstallPrompt && (
            <InstallPrompt onDismiss={() => dismissInstallPrompt(false)} />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
